import React, { type JSX } from 'react';
import Table, { type TableColumn } from '../Table/Table';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import VerticalContent from '../VerticalContent/VerticalContent';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '../../contexts/useIconDefaults';
import useTheme from '../../contexts/useTheme';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export type BuildsSecondaryGemsSectionViewModel = {
  title: string;
  description: string;
  table: {
    columns: Record<
      'item' | 'dps' | 'popularity',
      BuildsSecondaryGemsSectionViewModelColumn
    > &
      Partial<
        Record<'maxKey' | 'hps', BuildsSecondaryGemsSectionViewModelColumn>
      >;
    data: Array<{
      item: string;
      maxKey?: string;
      dps: string;
      hps?: string;
      popularity: string;
    }>;
  };
};

type BuildsSecondaryGemsSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  item: { size: iconSizeLarge },
};

export default function BuildsSecondaryGemsSection({
  title,
  description,
  table,
}: BuildsSecondaryGemsSectionViewModel): JSX.Element {
  const theme = useTheme();

  return (
    <VerticalContent>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {table.data.length > 0 ? (
          <Table
            {...{
              ...table,
              columns: {
                ...table.columns,
                item: {
                  ...table.columns.item,
                  maxWidth: 230,
                },
              },
            }}
            enhancedMarkdownEnabled={true}
            truncate={{
              limit: 4,
              showMoreButton: { styles: theme.primaryButtonStyles },
            }}
          />
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
